"use client"

import { useState, useEffect } from "react"
import API from "../utils/api"
import "./SalesReport.css"
import { format } from "date-fns"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"

const SalesReports = () => {
  const [activeTab, setActiveTab] = useState("today")
  const [isLoading, setIsLoading] = useState({ today: false, range: false })
  const [groupedToday, setGroupedToday] = useState([])
  const [groupedRange, setGroupedRange] = useState([])
  const [groupedAll, setGroupedAll] = useState([])
  const [currentView, setCurrentView] = useState("main")
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [generatingPDF, setGeneratingPDF] = useState({ today: false, range: false })
  const [generatingCSV, setGeneratingCSV] = useState({ today: false, range: false })
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [customerDetails, setCustomerDetails] = useState(null)
  const [pageByTab, setPageByTab] = useState({ today: 1, range: 1 })
  const [limit, setLimit] = useState(10)
  const [fromDate, setFromDate] = useState(format(new Date(), "yyyy-MM-dd"))
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"))

  useEffect(() => {
    loadToday()
    // initial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPageByTab((p) => ({ ...p, [activeTab]: 1 }))
  }, [activeTab])

  useEffect(() => {
    const clamp = (key, length) => {
      const total = Math.max(1, Math.ceil(length / limit))
      setPageByTab((p) => (p[key] > total ? { ...p, [key]: total } : p))
    }
    clamp('today', groupedToday.length)
    clamp('range', groupedRange.length)
  }, [groupedToday.length, groupedRange.length, limit])

  const normalizeSales = (salesArray) => {
    if (!Array.isArray(salesArray)) return []
    return salesArray.map((sale) => {
      const rawName = sale.partyName || sale.customerName || "Walk-in Customer"
      const partyName = typeof rawName === 'string' ? rawName.trim().replace(/\s+/g, ' ') : 'Walk-in Customer'
      const parsedQty = Number(sale.quantity)

      return {
        _id: sale._id || sale.productId || Math.random().toString(),
        productId: sale.productId || sale.product?._id,
        companyName: sale.companyName || sale.product?.companyName || "Unknown",
        modelNo: sale.modelNo || sale.product?.modelNo || "N/A",
        size: sale.size || sale.product?.size || undefined,
        // Only use per-transaction quantity; do NOT fallback to totalSold
        quantity: Number.isFinite(parsedQty) ? parsedQty : 0,
        // Preserve time-of-day for accurate sorting of latest uploads
        timestamp: sale.createdAt || sale.date || new Date().toISOString(),
        date: sale.date || sale.createdAt || new Date().toISOString(),
        partyName,
        notes: sale.notes || "",
      }
    })
  }

  const buildProductsFromGroupItems = (items = []) => {
    const map = new Map()
    for (const s of items) {
      const key = s.productId || `${s.companyName}::${s.modelNo}::${s.size || ''}`
      if (!map.has(key)) {
        map.set(key, {
          productId: s.productId,
          companyName: s.companyName,
          modelNo: s.modelNo,
          size: s.size,
          quantity: 0,
        })
      }
      const p = map.get(key)
      const q = Number(s.quantity)
      p.quantity += Number.isFinite(q) ? q : 0
    }
    return Array.from(map.values())
  }

  const groupByDateAndCustomer = (salesArray) => {
    const map = new Map()
    for (const s of salesArray) {
      const dateStr = s.date ? format(new Date(s.date), "yyyy-MM-dd") : "N/A"
      const displayName = typeof s.partyName === 'string' ? s.partyName.trim().replace(/\s+/g, ' ') : 'Walk-in Customer'
      const key = `${dateStr}::${displayName.toLowerCase()}`
      if (!map.has(key)) {
        map.set(key, {
          key,
          date: dateStr,
          customerName: displayName,
          items: [],
          totalQuantity: 0,
          notes: [],
          latestAt: undefined,
        })
      }
      const g = map.get(key)
      g.items.push(s)
      const q = Number(s.quantity)
      g.totalQuantity += Number.isFinite(q) ? q : 0
      if (s.notes && !g.notes.includes(s.notes)) g.notes.push(s.notes)
      const tsRaw = s.timestamp || s.date
      const tsVal = new Date(tsRaw).getTime()
      if (Number.isFinite(tsVal) && (!Number.isFinite(g.latestAt) || tsVal > g.latestAt)) {
        g.latestAt = tsVal
      }
    }
    const groups = Array.from(map.values())
    // Sort by numeric customer name sequence (e.g., 320, 321, 322)
    const withSequence = groups.map((g) => ({
      ...g,
      customerNumber: /^\d+$/.test(g.customerName) ? parseInt(g.customerName, 10) : Number.POSITIVE_INFINITY,
    }))
    withSequence.sort((a, b) => {
      if (a.customerNumber !== b.customerNumber) return a.customerNumber - b.customerNumber
      // Secondary tie-breakers to keep report stable
      if (a.date !== b.date) return a.date.localeCompare(b.date)
      const aVal = Number.isFinite(a.latestAt) ? a.latestAt : 0
      const bVal = Number.isFinite(b.latestAt) ? b.latestAt : 0
      return aVal - bVal
    })
    return withSequence
  }

  const generatePDF = (rows, title, filename) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
    doc.text(title, 105, 15, { align: "center" })
    autoTable(doc, {
      startY: 25,
      head: [["Date", "Customer", "Quantity"]],
      body: rows.map((r) => [r.date, r.customerName, r.totalQuantity]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [52, 152, 219] },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 80 },
        2: { cellWidth: 25, halign: 'right' },
      },
    })
    doc.save(filename)
  }

  const downloadPDF = (type) => {
    const now = new Date()
    let rows = []
    let title = ""
    let filename = ""
    if (type === "today") {
      rows = groupedToday
      title = `Sales Report - Today ${format(now, "dd MMM yyyy")}`
      filename = `sales-report-today-${format(now, "yyyy-MM-dd")}.pdf`
    } else {
      rows = groupedRange
      title = `Sales Report - Range`
      filename = `sales-report-range-${format(now, "yyyy-MM-dd")}.pdf`
    }

    if (!rows || rows.length === 0) return
    setGeneratingPDF((p) => ({ ...p, [type]: true }))
    try {
      generatePDF(rows, title, filename)
    } finally {
      setGeneratingPDF((p) => ({ ...p, [type]: false }))
    }
  }

  const downloadCSV = (type) => {
    const rows = type === 'today' ? groupedToday : groupedRange
    if (!rows || rows.length === 0) return
    setGeneratingCSV((p) => ({ ...p, [type]: true }))
    try {
      const header = ["Date", "Customer", "Quantity"]
      const body = rows.map((r) => [r.date, r.customerName, r.totalQuantity])
      const csv = [header, ...body].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `sales-${type}-${format(new Date(), "yyyy-MM-dd")}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      setGeneratingCSV((p) => ({ ...p, [type]: false }))
    }
  }

  const fetchToday = async () => {
    const res = await API.get("/products/sales/today")
    return normalizeSales(res.data)
  }

  const fetchRange = async (start, end) => {
    const res = await API.get(`/products/sales/range?start=${start}&end=${end}`)
    return normalizeSales(res.data)
  }

  const loadToday = async () => {
    setIsLoading((p) => ({ ...p, today: true }))
    try {
      const sales = await fetchToday()
      setGroupedToday(groupByDateAndCustomer(sales))
    } catch (err) {
      setGroupedToday([])
    } finally {
      setIsLoading((p) => ({ ...p, today: false }))
    }
  }

  const loadRange = async () => {
    setIsLoading((p) => ({ ...p, range: true }))
    try {
      const sales = await fetchRange(fromDate, toDate)
      setGroupedRange(groupByDateAndCustomer(sales))
    } catch (err) {
      setGroupedRange([])
    } finally {
      setIsLoading((p) => ({ ...p, range: false }))
    }
  }

  const loadAll = async () => {
    setIsLoading((p) => ({ ...p, all: true }))
    try {
      // Use backend summary by customer for "All"
      const res = await API.get("/products/sales/report/summary")
      const normalized = normalizeSales(res.data)
      setGroupedAll(groupByDateAndCustomer(normalized))
    } catch (err) {
      setGroupedAll([])
    } finally {
      setIsLoading((p) => ({ ...p, all: false }))
    }
  }

  // Customer details are derived from the selected row's items for the chosen date

  const onRowClick = async (group) => {
    setSelectedGroup(group)
    setCurrentView("details")
    setIsLoadingDetails(true)
    try {
      const items = Array.isArray(group?.items) ? group.items : []

      // Primary strategy: fetch this customer's sales and filter by selected date
      try {
        const res = await API.get(`/products/sales/customer/${encodeURIComponent(group.customerName)}`)
        const sales = Array.isArray(res?.data?.sales) ? res.data.sales : []
        const matches = sales.filter((s) => {
          const raw = s?.date || s?.createdAt || s?.saleDate || s?.invoiceDate
          if (!raw) return false
          const d = new Date(raw)
          if (isNaN(d.getTime())) return false
          return format(d, "yyyy-MM-dd") === group.date
        })
        if (matches.length > 0) {
          const aggregated = new Map()
          for (const s of matches) {
            const key = s.productId || `${s.companyName}::${s.modelNo || ''}::${s.size || ''}`
            if (!aggregated.has(key)) {
              aggregated.set(key, {
                productId: s.productId,
                companyName: s.companyName || "",
                modelNo: s.modelNo || "N/A",
                size: s.size || undefined,
                quantity: 0,
              })
            }
            const q = Number(s.quantity)
            aggregated.get(key).quantity += Number.isFinite(q) ? q : 0
          }
          // Enrich any missing model/size using product details
          const productsArr = Array.from(aggregated.values())
          const idsNeedingDetails = productsArr
            .map((p) => p.productId)
            .filter((id, idx) => id && productsArr[idx] && (!productsArr[idx].size || productsArr[idx].modelNo === 'N/A'))

          if (idsNeedingDetails.length > 0) {
            const detailPromises = idsNeedingDetails.map((id) =>
              API.get(`/products/${id}/details`).then((res) => ({ id, data: res.data })).catch(() => ({ id, data: null }))
            )
            const details = await Promise.all(detailPromises)
            const byId = new Map(details.map(({ id, data }) => [id, data]))
            for (const p of productsArr) {
              if (!p.productId) continue
              const d = byId.get(p.productId)
              if (d) {
                if (!p.size && d.size) p.size = d.size
                if (p.modelNo === 'N/A' && d.modelNo) p.modelNo = d.modelNo
                if (!p.companyName && d.companyName) p.companyName = d.companyName
              }
            }
          }
          setCustomerDetails({ products: productsArr })
          return
        }
      } catch (_) {
        // Ignore and fallback to local aggregation path below
      }

      // Aggregate quantities by productId when available and capture fallback display info
      const quantityByProductId = new Map()
      const fallbackByProductId = new Map()
      const itemsWithoutProductId = []

      for (const item of items) {
        const productId = item.productId
        const qty = Number(item.quantity)
        if (productId) {
          const prev = quantityByProductId.get(productId) || 0
          quantityByProductId.set(productId, prev + (Number.isFinite(qty) ? qty : 0))
          // Capture best-known display fields as a fallback if the details API fails
          const existing = fallbackByProductId.get(productId) || {}
          const sanitize = (val) => {
            if (typeof val !== 'string') return val
            const trimmed = val.trim()
            if (!trimmed || trimmed === 'N/A' || trimmed === 'Unknown') return undefined
            return trimmed
          }
          fallbackByProductId.set(productId, {
            companyName: existing.companyName || sanitize(item.companyName) || "",
            modelNo: existing.modelNo || sanitize(item.modelNo),
            size: existing.size || sanitize(item.size),
          })
        } else {
          itemsWithoutProductId.push(item)
        }
      }

      // Fetch product details to ensure model and size are populated
      const productIds = Array.from(quantityByProductId.keys())
      let enrichedProducts = []
      if (productIds.length > 0) {
        const detailPromises = productIds.map((id) =>
          API.get(`/products/${id}/details`)
            .then((res) => ({ id, data: res.data }))
            .catch(() => ({ id, data: null }))
        )
        const details = await Promise.all(detailPromises)
        enrichedProducts = details.map(({ id, data }) => {
          const fallback = fallbackByProductId.get(id) || {}
          const prefer = (primary, secondary) => {
            const p = typeof primary === 'string' ? primary.trim() : primary
            if (p && p !== 'N/A' && p !== 'Unknown') return p
            const s = typeof secondary === 'string' ? secondary.trim() : secondary
            return s
          }
          return {
            productId: id,
            companyName: prefer(data?.companyName, fallback.companyName) || "",
            modelNo: prefer(data?.modelNo, fallback.modelNo) || "N/A",
            size: prefer(data?.size, fallback.size) || undefined,
            quantity: quantityByProductId.get(id) || 0,
          }
        })
      }

      // Fallback aggregation for items that lack productId
      const fallbackProducts = buildProductsFromGroupItems(itemsWithoutProductId)

      setCustomerDetails({ products: [...enrichedProducts, ...fallbackProducts] })
    } finally {
      setIsLoadingDetails(false)
    }
  }

  const onBack = () => {
    setSelectedGroup(null)
    setCurrentView("main")
    setCustomerDetails(null)
    setIsLoadingDetails(false)
  }

  const renderTable = (rows, tabKey) => {
    if (rows.length === 0) return <p className="no-data">No records found.</p>
    return (
      <table className="report-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Date</th>
            <th>Customer</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const base = (pageByTab[tabKey] - 1) * limit
            const seq = base + idx + 1
            return (
              <tr key={r.key} className="clickable-row" onClick={() => onRowClick(r)}>
                <td>{seq}</td>
                <td>{r.date}</td>
                <td>{r.customerName}</td>
                <td>{r.totalQuantity}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  const getPaginated = (rows, tabKey) => {
    const start = (pageByTab[tabKey] - 1) * limit
    return rows.slice(start, start + limit)
  }

  const getTotalPages = (rows) => Math.max(1, Math.ceil(rows.length / limit))

  const renderPagination = (tabKey, rows) => {
    if (!rows || rows.length === 0) return null
    const totalPages = getTotalPages(rows)
    return (
      <div className="pagination">
        <button
          disabled={pageByTab[tabKey] === 1}
          onClick={() => setPageByTab((p) => ({ ...p, [tabKey]: p[tabKey] - 1 }))}
        >
          ⬅ Prev
        </button>
        <span>
          Page {pageByTab[tabKey]} of {totalPages}
        </span>
        <button
          disabled={pageByTab[tabKey] === totalPages}
          onClick={() => setPageByTab((p) => ({ ...p, [tabKey]: p[tabKey] + 1 }))}
        >
          Next ➡
        </button>
      </div>
    )
  }

  const renderControls = () => (
    <div className="report-controls">
      <label>Show: </label>
      <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPageByTab((p) => ({ ...p, [activeTab]: 1 })) }}>
        <option value={10}>10 per page</option>
        <option value={20}>20 per page</option>
        <option value={50}>50 per page</option>
      </select>
    </div>
  )

  if (isLoading.today || isLoading.range) {
    return (
      <div className="sales-reports-container">
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading sales data...</p>
        </div>
      </div>
    )
  }

  if (currentView === "details" && selectedGroup) {
    return (
      <div className="sale-details-container">
        <div className="sale-details-header">
          <button onClick={onBack} className="back-btn">← Back to Sales</button>
          <h2>Customer: {selectedGroup.customerName} — {selectedGroup.date}</h2>
        </div>
        <div className="sale-details-content">
          <div className="product-details-card">
            <h3>Products Sold</h3>
            {isLoadingDetails ? (
              <div className="loading">Loading customer details...</div>
            ) : !customerDetails ? (
              <div className="empty">No details available</div>
            ) : (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Model</th>
                    <th>Size</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {customerDetails.products?.map((p) => (
                    <tr key={p.productId || `${p.companyName}-${p.modelNo}`}>
                      <td>{p.companyName || '-'}</td>
                      <td>{p.modelNo}</td>
                      <td>{p.size || '-'}</td>
                      <td>{p.quantity}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Total</td>
                    <td>{customerDetails.products?.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0) || 0}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-reports-container">
      <div className="page-header">
        <h2>📊 Sales Reports</h2>
      </div>
      <div className="sales-reports-section">
        <div className="tabs">
          <button className={`tab-btn ${activeTab === "today" ? "active" : ""}`} onClick={() => setActiveTab("today")}>Today</button>
          <button className={`tab-btn ${activeTab === "range" ? "active" : ""}`} onClick={() => setActiveTab("range")}>From–To</button>
        </div>
        {activeTab === "today" && (
          <div className="today-sales">
            <h3>📅 Today</h3>
            <div className="report-actions">
              <button
                className="download-btn"
                disabled={generatingPDF.today || groupedToday.length === 0}
                onClick={() => downloadPDF('today')}
              >
                {generatingPDF.today ? "Generating..." : "Download PDF"}
              </button>
              <button
                className="download-btn"
                disabled={generatingCSV.today || groupedToday.length === 0}
                onClick={() => downloadCSV('today')}
              >
                {generatingCSV.today ? "Generating..." : "Download Excel"}
              </button>
            </div>
            {renderControls()}
            {renderTable(getPaginated(groupedToday, 'today'), 'today')}
            {renderPagination('today', groupedToday)}
          </div>
        )}
        {activeTab === "range" && (
          <div className="range-sales">
            <h3>📅 From–To</h3>
            <div className="range-controls">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              <span className="separator">to</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              <button className="apply-btn" onClick={loadRange}>Apply</button>
            </div>
            <div className="report-actions">
              <button
                className="download-btn"
                disabled={generatingPDF.range || groupedRange.length === 0}
                onClick={() => downloadPDF('range')}
              >
                {generatingPDF.range ? "Generating..." : "Download PDF"}
              </button>
              <button
                className="download-btn"
                disabled={generatingCSV.range || groupedRange.length === 0}
                onClick={() => downloadCSV('range')}
              >
                {generatingCSV.range ? "Generating..." : "Download Excel"}
              </button>
            </div>
            {renderControls()}
            {renderTable(getPaginated(groupedRange, 'range'), 'range')}
            {renderPagination('range', groupedRange)}
          </div>
        )}
      </div>
    </div>
  )
}

export default SalesReports
